import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAdmin = async (userId: string) => {
    try {
      const rpcPromise = supabase.rpc("has_role", {
        _user_id: userId,
        _role: "admin",
      });
      const timeoutPromise = new Promise<never>((_, reject) => {
        window.setTimeout(() => reject(new Error("timeout")), 4000);
      });
      const { data, error } = (await Promise.race([rpcPromise, timeoutPromise])) as Awaited<typeof rpcPromise>;
      if (error) {
        console.error("[Auth] RPC has_role:", error.message, "— ¿aplicaste las migraciones y GRANT en Supabase?");
        setIsAdmin(false);
        return;
      }
      setIsAdmin(!!data);
    } catch (err) {
      console.error("[Auth] checkAdmin falló:", err);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    const applySession = (nextSession: Session | null) => {
      try {
        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        if (nextSession?.user) {
          // No bloqueamos la UI por una comprobación auxiliar de rol.
          void checkAdmin(nextSession.user.id);
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        console.error("[Auth] Error aplicando sesión:", err);
        setSession(null);
        setUser(null);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, nextSession) => {
        // Evita “pantalla cargando” en refrescos de token al volver de pestañas/minimizar.
        if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
          setLoading(true);
        }
        applySession(nextSession);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setLoading(true);
      applySession(session);
    }).catch((err) => {
      console.error("[Auth] getSession falló:", err);
      setSession(null);
      setUser(null);
      setIsAdmin(false);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ session, user, isAdmin, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
